import axios, {
  type AxiosError,
  HttpStatusCode,
  type InternalAxiosRequestConfig,
  isAxiosError
} from 'axios'
import i18next from 'i18next'
import type { Exception } from 'types/api'
import rollbar from '../rollbarConfig'
import { ApiError } from './utils/ApiError'
import { userStorage } from './utils/local-storage'

declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    _authRetried?: boolean
  }
}

const REQUEST_TIMEOUT = 60_000

let tokenRefreshPromise: Promise<boolean> | undefined

// if (!i18next.hasResourceBundle(i18next.language, 'auth')) {
//   await i18next.loadNamespaces('auth')
// }

const getBaseUrl = () => {
  const baseUrl = import.meta.env.VITE_BASE_URL
  // const language = i18next.language || 'sk'
  // return `${baseUrl}${language}/`
  return `${baseUrl}/`
}

const apiClient = axios.create({
  baseURL: import.meta.env.VITE_BASE_URL,
  withCredentials: false,
  timeout: REQUEST_TIMEOUT,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: userStorage.getBearer(),
    'Accept-Language': i18next.language || 'sk'
  },
  paramsSerializer: {
    indexes: null // This will serialize arrays as status=AVAILABLE&status=DRAFT instead of status[]=AVAILABLE
  }
})

// #region Request interceptors
const requestInterceptors = apiClient.interceptors.request
/**
 * Important: REQUEST interceptors must be registered in the reversed order of execution.
 * This is not applicable to RESPONSE intereceptors, it's just a quirk of Axios.
 */
requestInterceptors.use(addAuthorization)
requestInterceptors.use(waitForToken)
requestInterceptors.use(refreshToken)
requestInterceptors.use(logRequest)

/**
 * Logs request using application logger.
 *
 * @param config Axios request config.
 * @returns Axios request config.
 */
function logRequest(
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig {
  return config
}

/**
 * Attempts to perform a token refresh request if the token is expired.
 * If refresh token is not found – immediately rejects `tokenRefreshPromise` which will make
 * `waitForToken` interceptor to throw an `Error`.
 *
 * Does nothing if the token refresh is already in progress.
 * This function will ignore all requests with url starting with `/v2/auth/`.
 *
 * @param config Axios request config.
 * @returns Axios request config.
 */
function refreshToken(
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig {
  if (
    isRefreshingToken() ||
    !userStorage.isExpired() ||
    isAuthURL(config.url)
  ) {
    return config
  }

  tokenRefreshPromise = getTokenRefreshPromise()

  return config
}

/**
 * Blocks requests while the token refresh is in progress.
 * If the token refresh fails – throws an `Error` and rejects any pending requests.
 *
 * This function will not block requests with url starting with `/v2/auth/`.
 *
 * @param config Axios request config.
 * @returns Axios request config.
 */
async function waitForToken(
  config: InternalAxiosRequestConfig
): Promise<InternalAxiosRequestConfig> {
  if (!isRefreshingToken() || isAuthURL(config.url)) {
    return config
  }

  const promise = tokenRefreshPromise
  const isRefreshed = await promise
  if (tokenRefreshPromise === promise) {
    tokenRefreshPromise = undefined
  }

  if (isRefreshed) {
    return config
  }

  throw new Error('Token refresh failed. Please log in again.')
}

/**
 * Adds `Authorization` header to each request.
 *
 * @param config Axios request config.
 * @returns Axios request config.
 */
function addAuthorization(
  config: InternalAxiosRequestConfig
): InternalAxiosRequestConfig {
  config.headers.Authorization = userStorage.getBearer()
  config.headers['Accept-Language'] = i18next.language || 'sk'

  // Update the base URL for each request
  config.baseURL = getBaseUrl()

  return config
}
// #endregion

// #region Response interceptors
apiClient.interceptors.response.use(null, handleFailedRequest)

async function handleFailedRequest(error: AxiosError<Exception>) {
  if (isAxiosError(error)) {
    if (error.response) {
      const status = error.response.status

      // Report server errors (5xx) to Rollbar
      if (status >= 500) {
        rollbar.error('API Server Error', {
          status,
          url: error.config?.url,
          method: error.config?.method,
          responseData: error.response.data
        })
      }

      // Single retry after 401: refresh once (single-flight), replay the
      // original request with the new token. All request bodies in this app
      // (JSON strings, browser FormData, blob downloads) are replayable.
      if (
        status === HttpStatusCode.Unauthorized &&
        error.config &&
        !isAuthURL(error.config.url) &&
        !error.config._authRetried
      ) {
        error.config._authRetried = true

        // Fast path: another tab or a concurrent refresh already rotated the
        // token — the stored bearer differs from the one that just failed.
        const failedBearer = error.config.headers?.Authorization
        const currentBearer = userStorage.getBearer()
        if (
          currentBearer &&
          currentBearer !== failedBearer &&
          !userStorage.isExpired()
        ) {
          return apiClient(error.config)
        }

        if (await joinOrStartTokenRefresh()) {
          return apiClient(error.config)
        }
        // Refresh failed — logout (if warranted) already happened inside
        // getTokenRefreshPromise; fall through so the caller still rejects.
      }

      // Handle Blob responses (e.g., from endpoints with responseType: 'blob')
      if (error.response.data instanceof Blob) {
        try {
          const text = await error.response.data.text()
          const errorData = JSON.parse(text)
          return Promise.reject(
            new ApiError(error, errorData.message, errorData.extra)
          )
        } catch {
          // If parsing fails, continue with default error handling
        }
      }

      switch (error.response.status) {
        case HttpStatusCode.Unauthorized:
          // Refresh-endpoint failures are handled inside getTokenRefreshPromise
          // (multi-tab recovery + logout decision). Reaching here with
          // _authRetried set means the replay with a fresh token was also
          // rejected — the session is dead.
          if (error.config?._authRetried && !isAuthURL(error.config.url)) {
            logout()
          }
          return Promise.reject(
            new ApiError(
              error,
              error.response.data.message,
              error.response.data.extra
            )
          )

        case HttpStatusCode.Forbidden:
          // logout()
          console.error('Forbidden error', error)
          break

        default:
          return Promise.reject(
            new ApiError(
              error,
              error.response.data.message,
              error.response.data.extra
            )
          )
      }
    }
  }

  // Fallback to i18n translation
  return Promise.reject(
    new ApiError(
      error,
      i18next.t('login.backend.not_active_user_with_this_credentials', {
        ns: 'auth'
      })
    )
  )
}

//  #endregion

// #region Utils
function isAuthURL(url?: string): boolean {
  if (!url) return false

  return (
    url.startsWith('/api/v1/referrals/check-availability') ||
    url.startsWith('/api/v1/auth')
  )
}
function isRefreshingToken() {
  return !!tokenRefreshPromise
}

function getTokenRefreshPromise(): Promise<boolean> {
  return new Promise(function tryRefreshToken(resolve) {
    const refreshTokenUsed = userStorage.getRefresh()

    if (!refreshTokenUsed) {
      resolve(false)
      return
    }

    apiClient
      .post('/api/v1/auth/refresh', { refresh: refreshTokenUsed })
      .then(function onRefreshSuccess(response) {
        userStorage.setTokens(response.data)
        resolve(true)
      })
      .catch(function onRefreshError(error) {
        // Multi-tab race: with ROTATE_REFRESH_TOKENS + BLACKLIST_AFTER_ROTATION
        // another tab may have already rotated the token we just tried to use.
        // If storage now holds a different, non-expired pair, adopt it.
        const latestRefresh = userStorage.getRefresh()
        if (
          latestRefresh &&
          latestRefresh !== refreshTokenUsed &&
          !userStorage.isExpired()
        ) {
          resolve(true)
          return
        }

        // Only a definitive 401 from the refresh endpoint means the session
        // is dead. Network errors / 5xx: fail this batch, keep the session.
        const source = error instanceof ApiError ? error.source : error
        if (
          isAxiosError(source) &&
          source.response?.status === HttpStatusCode.Unauthorized
        ) {
          logout()
        }
        console.error('Failed to refresh token', error)
        resolve(false)
      })
  })
}

/**
 * Joins the in-flight token refresh or starts a new one (single-flight):
 * N concurrent callers cause exactly one refresh request.
 */
async function joinOrStartTokenRefresh(): Promise<boolean> {
  if (!isRefreshingToken()) {
    tokenRefreshPromise = getTokenRefreshPromise()
  }

  const promise = tokenRefreshPromise
  const refreshed = await promise
  if (tokenRefreshPromise === promise) {
    tokenRefreshPromise = undefined
  }
  return refreshed ?? false
}
// #endregion

function logout() {
  localStorage.clear()
  window.location.replace('/login')
}

/**
 * Ensures a non-expired access token is in localStorage before calling
 * non-axios APIs (e.g. livability fetch). Mirrors axios request interceptors.
 */
async function ensureValidAccessToken(force = false): Promise<boolean> {
  if (!userStorage.get()) {
    return false
  }

  if (!force && !userStorage.isExpired()) {
    return true
  }

  return joinOrStartTokenRefresh()
}

export { apiClient, ensureValidAccessToken }
